import { DocumentNode } from 'graphql';
import { cloneDeep } from 'lodash';
import { DataProps, graphql, MutateProps, OperationOption, QueryOpts } from 'react-apollo'; // eslint-disable-line no-restricted-imports

// This HOC exists as a wrapper around the `graphql` HOC provided by react-apollo so that we can
// set our own default options across all queries.
//
// All these types look complicated, but they are the same generics specified for the `graphql` HOC
// and they are just passed through this function.
export function withGraphql<
  TProps extends TGraphQLVariables | unknown = unknown,
  TData = unknown,
  TGraphQLVariables = unknown,
  TChildProps = Partial<DataProps<TData, TGraphQLVariables>> & Partial<MutateProps<TData, TGraphQLVariables>>,
>(document: DocumentNode, queryOptions?: OperationOption<TProps, TData, TGraphQLVariables, TChildProps>) {
  // Creates a deep copy of queryOptions so we can avoid mutating the original object, preventing
  // infinite recursion.
  const optionsWithDefaults = cloneDeep(queryOptions) || {};

  // props isn't accessible in this context, so instead we set `options` to be a function that will
  // be evaluated later when Apollo does have access to props.
  optionsWithDefaults.options = (props: TProps) => {
    let safeOptions = {} as QueryOpts<TGraphQLVariables>;

    if (typeof queryOptions !== 'undefined') {
      if (typeof queryOptions.options === 'object') {
        safeOptions = queryOptions.options;
      }

      if (typeof queryOptions.options === 'function') {
        safeOptions = queryOptions.options(props);
      }
    }

    if (!Object.prototype.hasOwnProperty.call(safeOptions, 'ssr')) {
      safeOptions.ssr = false;
    }

    return safeOptions;
  };

  return (graphql(document, optionsWithDefaults));
}
